"use client";

import posthog, { type PostHogConfig } from "posthog-js";
import {
  SENSITIVE_ANALYTICS_QUERY_PARAMS,
  sanitizeAnalyticsNetworkRequest,
  sanitizeAnalyticsEventProperties,
  sanitizeAnalyticsText,
  sanitizeAnalyticsUrl,
} from "@/lib/productAnalyticsPrivacy.mjs";
import {
  getSessionReplayTransition,
  shouldRecordProductSession,
} from "@/lib/productAnalyticsReplayPolicy.mjs";

type AnalyticsProperties = Record<string, unknown>;
type SanitizeOptions = {
  preservePostHogInternals?: boolean;
};

type AnalyticsUser = {
  userId?: string | null;
  role?: string | null;
  preferredLanguage?: string | null;
  organizationProfileCompleted?: boolean | null;
  onboardingStatus?: string | null;
  organization?: {
    sector?: string | null;
    profileCompleted?: boolean | null;
    onboardingStatus?: string | null;
    provisioningSource?: string | null;
  } | null;
};

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY || "";
const POSTHOG_HOST = "/qv";
const POSTHOG_ENVIRONMENT =
  process.env.NEXT_PUBLIC_POSTHOG_ENVIRONMENT ||
  (process.env.NODE_ENV === "production" ? "production" : "staging");
const CAPTURE_CONSOLE_ERRORS =
  process.env.NEXT_PUBLIC_POSTHOG_CAPTURE_CONSOLE_ERRORS !== "false";
const ENABLE_RECORDING_CONSOLE_LOGS =
  process.env.NEXT_PUBLIC_POSTHOG_RECORD_CONSOLE_LOGS === "true";
const ENABLE_NATIVE_GROUP_ANALYTICS =
  process.env.NEXT_PUBLIC_POSTHOG_ENABLE_GROUP_ANALYTICS === "true";
const ATTRIBUTION_STORAGE_KEY = "qac.productAttribution.v1";
const MAX_ATTRIBUTION_VALUE_LENGTH = 160;

const SENSITIVE_QUERY_PARAMS = [...SENSITIVE_ANALYTICS_QUERY_PARAMS];

const SENSITIVE_EXACT_KEYS = new Set([
  "body",
  "company_name",
  "content",
  "cookie",
  "document",
  "documents",
  "email",
  "full_name",
  "headers",
  "log",
  "logs",
  "message",
  "name",
  "org_name",
  "organization_name",
  "output",
  "outputs",
  "payload",
  "phone",
  "username",
]);

const SENSITIVE_KEY_FRAGMENTS = [
  "access_token",
  "api_key",
  "apikey",
  "authorization",
  "client_secret",
  "connection_string",
  "connectionstring",
  "id_token",
  "password",
  "private_key",
  "refresh_token",
  "secret",
  "token",
];

const POSTHOG_INTERNAL_PROPERTY_KEYS = new Set([
  "$api_key",
  "$device_id",
  "$session_id",
  "$token",
  "$window_id",
  "api_key",
  "distinct_id",
  "token",
]);

const IGNORED_EXCEPTION_PATTERNS = [
  /the resource .* was preloaded using link preload/i,
  /removing .*impermanent intrinsics/i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-web-extension:\/\//i,
];

const ATTRIBUTION_QUERY_PARAM_MAP: Record<string, string> = {
  account: "target_account",
  campaign: "outreach_campaign",
  channel: "outreach_channel",
  company: "target_account",
  lead_company: "target_account",
  lead_id: "outreach_id",
  linkedin_id: "linkedin_id",
  li_fat_id: "linkedin_click_id",
  outreach_id: "outreach_id",
  source: "outreach_source",
  target_account: "target_account",
  target_company: "target_account",
  utm_campaign: "utm_campaign",
  utm_content: "utm_content",
  utm_id: "utm_id",
  utm_medium: "utm_medium",
  utm_source: "utm_source",
  utm_term: "utm_term",
};

let initialized = false;
let linkedinQaOverride = false;
let sessionRecordingActive: boolean | null = null;

type ProductAnalyticsInitOptions = {
  linkedinQa?: boolean;
};

function getReplaySampleRate() {
  // Full capture is an explicit product requirement. Stale build secrets must
  // not silently override the project setting with a sampled/zero rate.
  return 1;
}

function getLocaleFromPath(pathname: string) {
  const locale = pathname.split("/").filter(Boolean)[0];
  return locale === "en" || locale === "es" ? locale : undefined;
}

function getRoute() {
  if (typeof window === "undefined") return "";
  return window.location.pathname;
}

function getDeviceType() {
  if (typeof window === "undefined") return "desktop";
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

function shouldDropProperty(key: string, options: SanitizeOptions = {}) {
  const normalizedKey = key.toLowerCase();
  if (
    options.preservePostHogInternals &&
    POSTHOG_INTERNAL_PROPERTY_KEYS.has(normalizedKey)
  ) {
    return false;
  }

  return (
    SENSITIVE_EXACT_KEYS.has(normalizedKey) ||
    SENSITIVE_KEY_FRAGMENTS.some((fragment) => normalizedKey.includes(fragment))
  );
}

function truncateAttributionValue(value: string) {
  const cleanValue = value.trim();
  if (cleanValue.length <= MAX_ATTRIBUTION_VALUE_LENGTH) return cleanValue;
  return cleanValue.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH);
}

function readStoredAttributionProperties() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as AnalyticsProperties)
      : {};
  } catch {
    return {};
  }
}

function writeStoredAttributionProperties(properties: AnalyticsProperties) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(properties),
    );
  } catch {
    // Ignore storage failures; analytics should never break the product flow.
  }
}

function getCurrentAttributionProperties() {
  if (typeof window === "undefined") return {};

  const url = new URL(window.location.href);
  const current: AnalyticsProperties = {};

  Object.entries(ATTRIBUTION_QUERY_PARAM_MAP).forEach(
    ([queryParam, property]) => {
      const value = url.searchParams.get(queryParam);
      if (!value) return;
      current[property] = truncateAttributionValue(value);
    },
  );

  if (Object.keys(current).length > 0) {
    current.attribution_landing_path = window.location.pathname;
    current.attribution_landing_url = sanitizeUrl(window.location.href);
    if (document.referrer) {
      current.initial_referrer = sanitizeUrl(document.referrer);
    }
    current.attribution_captured_at = new Date().toISOString();
  }

  const stored = readStoredAttributionProperties();
  const merged = { ...stored, ...current };
  if (Object.keys(current).length > 0) {
    writeStoredAttributionProperties(merged);
  }

  return merged;
}

function sanitizeUrl(value: string) {
  const baseUrl =
    typeof window === "undefined"
      ? "https://analytics.invalid"
      : window.location.origin;
  return sanitizeAnalyticsUrl(value, baseUrl);
}

function sanitizeValue(value: unknown, options: SanitizeOptions = {}): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, options));
  }
  if (value && typeof value === "object") {
    return sanitizeProperties(value as AnalyticsProperties, options);
  }
  if (typeof value === "string") {
    return sanitizeAnalyticsText(value);
  }
  return value;
}

export function sanitizeProperties(
  properties: AnalyticsProperties = {},
  options: SanitizeOptions = {},
) {
  return Object.entries(properties).reduce<AnalyticsProperties>(
    (acc, [key, value]) => {
      if (shouldDropProperty(key, options) || value === undefined) return acc;

      if (
        typeof value === "string" &&
        (key.toLowerCase().includes("url") ||
          key === "$current_url" ||
          key === "$referrer")
      ) {
        acc[key] = sanitizeUrl(value);
        return acc;
      }

      acc[key] = sanitizeValue(value, options);
      return acc;
    },
    {},
  );
}

function buildDefaultProperties(properties: AnalyticsProperties = {}) {
  const route = getRoute();
  const companyId =
    typeof properties.company_id === "string"
      ? properties.company_id
      : undefined;

  return sanitizeProperties({
    ...getCurrentAttributionProperties(),
    environment: POSTHOG_ENVIRONMENT,
    route,
    source: "web_app",
    device_type: getDeviceType(),
    locale: getLocaleFromPath(route),
    ...properties,
    ...(companyId ? { $groups: { company: companyId } } : {}),
  });
}

function stringifyExceptionValue(value: unknown): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.map(stringifyExceptionValue).join(" ");
  }
  if (value instanceof Error) {
    return `${value.name} ${value.message} ${value.stack || ""}`;
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return [
      record.message,
      record.name,
      record.type,
      record.value,
      record.stack,
      record.url,
      record.filename,
      record.abs_path,
    ]
      .map(stringifyExceptionValue)
      .filter(Boolean)
      .join(" ");
  }
  return String(value);
}

function shouldIgnoreException(errorOrProperties: unknown) {
  const text = stringifyExceptionValue(errorOrProperties);
  if (!text) return false;
  return IGNORED_EXCEPTION_PATTERNS.some((pattern) => pattern.test(text));
}

function sanitizeBeforeSend(event: any) {
  if (!event?.properties) return event;

  if (
    event.event === "$exception" &&
    shouldIgnoreException([
      event.properties.$exception_message,
      event.properties.$exception_type,
      event.properties.$exception_list,
      event.properties.$current_url,
      event.properties.$exception_source,
    ])
  ) {
    return null;
  }

  return sanitizeAnalyticsEventProperties(event, sanitizeProperties);
}

function syncSessionRecording(shouldRecord: boolean) {
  const transition = getSessionReplayTransition(
    sessionRecordingActive,
    shouldRecord,
  );
  if (transition === "start") posthog.startSessionRecording();
  if (transition === "stop") posthog.stopSessionRecording();
  sessionRecordingActive = shouldRecord;
}

export function initProductAnalytics(
  options: ProductAnalyticsInitOptions = {},
) {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;

  if (typeof options.linkedinQa === "boolean") {
    linkedinQaOverride = options.linkedinQa;
  }
  const shouldRecordSession = shouldRecordProductSession(
    getRoute(),
    linkedinQaOverride,
  );

  if (initialized) {
    syncSessionRecording(shouldRecordSession);
    return;
  }

  const config: Partial<PostHogConfig> = {
    api_host: POSTHOG_HOST,
    on_request_error: (response) => {
      const code = typeof response.text === "string" ? response.text.match(/^Capture rejected \(([A-Z]+)\)$/)?.[1] : undefined;
      console.warn("[Quantum analytics] Transport failed", { status: response.statusCode, ...(code ? { code } : {}) });
    },
    ui_host: "https://us.posthog.com",
    defaults: "2026-01-30",
    capture_pageview: "history_change",
    capture_pageleave: true,
    autocapture: false,
    capture_heatmaps: true,
    capture_performance: { network_timing: false, web_vitals: true },
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: CAPTURE_CONSOLE_ERRORS,
    },
    disable_session_recording: !shouldRecordSession,
    enable_recording_console_log: ENABLE_RECORDING_CONSOLE_LOGS,
    mask_personal_data_properties: true,
    custom_personal_data_properties: SENSITIVE_QUERY_PARAMS,
    before_send: sanitizeBeforeSend,
    advanced_feature_flags_dedup_per_session: true,
    __preview_deferred_init_extensions: false,
    session_recording: {
      blockClass: "ph-no-capture",
      blockSelector:
        ".ph-no-capture, [data-ph-no-capture], [data-sensitive='true']",
      maskAllInputs: true,
      maskTextSelector:
        "[data-qac-app='true'] *, .ph-mask, [data-ph-mask='true']",
      recordBody: false,
      recordHeaders: false,
      sampleRate: getReplaySampleRate(),
      maskCapturedNetworkRequestFn: (request) =>
        sanitizeAnalyticsNetworkRequest(request, window.location.origin),
    },
  };

  posthog.init(POSTHOG_KEY, config);
  initialized = true;
  sessionRecordingActive = shouldRecordSession;

  const attributionProperties = sanitizeProperties(
    getCurrentAttributionProperties(),
  );
  if (Object.keys(attributionProperties).length > 0) {
    posthog.register(attributionProperties);
  }
}

export function isProductAnalyticsEnabled() {
  return Boolean(POSTHOG_KEY);
}

export function track(eventName: string, properties: AnalyticsProperties = {}) {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  initProductAnalytics();
  posthog.capture(eventName, buildDefaultProperties(properties));
}

export function captureAppException(
  error: unknown,
  properties: AnalyticsProperties = {},
) {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  if (shouldIgnoreException(error) || shouldIgnoreException(properties)) return;

  try {
    initProductAnalytics();
    posthog.captureException(
      error,
      buildDefaultProperties({
        feature_area: "unknown",
        route: getRoute(),
        success: false,
        ...properties,
      }),
    );
  } catch {
    // Analytics should never break the product flow.
  }
}

export function identifyUser(
  currentUser: AnalyticsUser | null | undefined,
  activeOrgId?: string | null,
  runtimeEnvironment?: string | null,
) {
  if (typeof window === "undefined" || !POSTHOG_KEY || !currentUser?.userId) {
    return;
  }

  initProductAnalytics();

  const companyId = activeOrgId || undefined;
  const safeProperties = buildDefaultProperties({
    company_id: companyId,
    user_role: currentUser.role || undefined,
    locale: currentUser.preferredLanguage || getLocaleFromPath(getRoute()),
    organization_profile_completed:
      currentUser.organizationProfileCompleted ??
      currentUser.organization?.profileCompleted ??
      undefined,
    onboarding_status:
      currentUser.onboardingStatus ??
      currentUser.organization?.onboardingStatus ??
      undefined,
    provisioning_source:
      currentUser.organization?.provisioningSource || undefined,
    industry: currentUser.organization?.sector || undefined,
    runtime_environment: runtimeEnvironment || undefined,
  });

  posthog.identify(currentUser.userId, safeProperties);

  if (companyId) {
    setCompanyGroup(companyId, {
      industry: currentUser.organization?.sector || undefined,
      organization_profile_completed:
        currentUser.organizationProfileCompleted ??
        currentUser.organization?.profileCompleted ??
        undefined,
      onboarding_status:
        currentUser.onboardingStatus ??
        currentUser.organization?.onboardingStatus ??
        undefined,
      runtime_environment: runtimeEnvironment || undefined,
    });
  }
}

export function setCompanyGroup(
  companyId: string | null | undefined,
  properties: AnalyticsProperties = {},
) {
  if (typeof window === "undefined" || !POSTHOG_KEY || !companyId) return;
  initProductAnalytics();

  const safeProperties = sanitizeProperties({
    company_id: companyId,
    $groups: { company: companyId },
    ...properties,
  });

  posthog.register(safeProperties);

  if (ENABLE_NATIVE_GROUP_ANALYTICS) {
    posthog.group("company", companyId, sanitizeProperties(properties));
  }
}

export function resetAnalytics() {
  if (typeof window === "undefined" || !POSTHOG_KEY) return;
  initProductAnalytics();
  posthog.reset();
}

export function trackErrorShown({
  error_code = "unknown_error",
  feature_area = "unknown",
  route,
}: {
  error_code?: string;
  feature_area?: string;
  route?: string;
}) {
  track("error_message_shown", {
    error_code,
    feature_area,
    route: route || getRoute(),
    success: false,
  });
}

export function getAnalyticsErrorCode(
  error: unknown,
  fallback = "unknown_error",
) {
  if (error && typeof error === "object" && "name" in error) {
    const name = String((error as { name?: unknown }).name || "").trim();
    if (name) return name;
  }
  if (error instanceof Error && error.message) {
    return error.message.slice(0, 80).replace(/[^a-zA-Z0-9_-]+/g, "_");
  }
  return fallback;
}
